import Select from 'react-select';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from '@components/Shared/Loader/Loading';
import { Card, CardFooter, CardHeader } from '@components/card/card';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { initReport, getReportPagedData, selectReport, setReportRequest } from '@store/DynamicSlice/reportTableSlice';
import ReportExportExcelModal, { type ExportColumn } from '@Helper/ReportPaged/ReportExportExcelModal';
import ReportPagination from '@Helper/ReportPaged/ReportPagination';
import useGlChartLookups from '@hooks/Finance/ChartAccountsDetails/useGlChartLookups';
import useGetApExpenseCategoryList from '@hooks/Finance/Expense/useGetApExpenseCategoryList';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';

/** =========================
 * INNER REQUEST SHAPE
 * ========================= */
type ExpensesDetailsRequestDto = {
  pageIndex?: number;
  pageSize?: number;
  fromDate?: string | null;
  toDate?: string | null;
  organizationId?: string | null;
  expenseCategoryId?: string | null;
  amountFrom?: number | null;
  amountTo?: number | null;
};

/** =========================
 * RESPONSE SHAPE (Array items)
 * ========================= */
type RowDto = {
  expensE_CATEGORY_ID: string | null;
  categorY_NAME: string | null;
  categorY_NAME2: string | null;
  expensE_CODE: string | null;
  amount: number;
  vat: number;
  totaL_WITH_VAT: number;
  expensE_DATE: string | null;
  description: string | null;
  organizatioN_ID: string | null;
  orG_NAME: string | null;
};

const REPORT_KEY = 'RPT_ExpensesDetails';
const ENDPOINT = 'FinReports/GetExpensesDetailsPaged';

const safe = (v: any, fallback = '—') => (v === null || v === undefined || v === '' ? fallback : String(v));

const formatNumber = (num: number, digits: number = 2) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(num || 0);
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

const getOptId = (opt: any): string | null => {
  const v = opt?.value ?? opt?.id ?? opt?.ID ?? opt?.key ?? null;
  return v ? String(v) : null;
};

const isoOrNull = (d: any) => {
  if (!d) return null;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return `${d}T00:00:00.000Z`;
  if (d instanceof Date) return d.toISOString();
  return null;
};

const cleanNulls = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined || v === '') continue;
    out[k] = v;
  }
  return out;
};

const ExpensesDetails_Paged = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  // options
 
  const { OperatingUnitOptions } = useGetOperatingUnitWithPermission();
  const { OptionsExpenseCategory } = useGetApExpenseCategoryList();

  // TODO: استبدل هذا بـ Hook الخاص بجلب فئات المصروفات (Expense Categories)
//   const OptionsExpenseCategories: any[] = [];
//   const loadingCategories = false;

  // Filters state
  const [organizationFilter, setOrganizationFilter] = useState<any>(null);
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState<any>(null);

  // Date range
  const [fromDate, setFromDate] = useState<any>(null);
  const [toDate, setToDate] = useState<any>(null);

  // Amount range
  const [amountFrom, setAmountFrom] = useState<number | ''>('');
  const [amountTo, setAmountTo] = useState<number | ''>('');

  const [showExport, setShowExport] = useState(false);

  // report state
  const report = useAppSelector(s => selectReport(s as any, REPORT_KEY));
  const items = (report?.items ?? []) as RowDto[];
  const meta = report?.meta ?? null;
  const loading = report?.loading ?? false;
  const error = report?.error ?? null;
  const isAr = i18n.language !== 'en';

  const activeFiltersCount = useMemo(() => {
    let c = 0;
    if (organizationFilter) c++;
    if (expenseCategoryFilter) c++;
    if (fromDate) c++;
    if (toDate) c++;
    if (amountFrom !== '') c++;
    if (amountTo !== '') c++;
    return c;
  }, [organizationFilter, expenseCategoryFilter, fromDate, toDate, amountFrom, amountTo]);

  const exportColumns: ExportColumn<RowDto>[] = [
    { headerAr: 'رقم المصروف', headerEn: 'Expense Code', value: r => r.expensE_CODE || '' },
    { headerAr: 'التاريخ', headerEn: 'Date', value: r => formatDate(r.expensE_DATE) },
    { headerAr: 'الفرع/المنظمة', headerEn: 'Organization', value: r => r.orG_NAME || '' },
    {
      headerAr: 'فئة المصروف',
      headerEn: 'Category',
      value: r => (isAr ? r.categorY_NAME2 || r.categorY_NAME : r.categorY_NAME || r.categorY_NAME2) || ''
    },
    { headerAr: 'المبلغ', headerEn: 'Amount', value: r => r.amount || 0 },
    { headerAr: 'الضريبة', headerEn: 'VAT', value: r => r.vat || 0 },
    { headerAr: 'الإجمالي مع الضريبة', headerEn: 'Total with VAT', value: r => r.totaL_WITH_VAT || 0 },
    { headerAr: 'الوصف', headerEn: 'Description', value: r => r.description || '' }
  ];

  useEffect(() => {
    const initialRequest: ExpensesDetailsRequestDto = {
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(
      initReport({
        reportKey: REPORT_KEY,
        endpoint: ENDPOINT,
        initialRequest
      })
    );

    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  }, [dispatch]);

  const applyFilters = () => {
    const orgId = getOptId(organizationFilter);
    const catId = getOptId(expenseCategoryFilter);

    const prevReq = (((report as any)?.request ?? {}) as ExpensesDetailsRequestDto) || {};

    const nextReq: ExpensesDetailsRequestDto = {
      ...prevReq,
      pageIndex: 0,
      pageSize: prevReq.pageSize ?? 50,
      fromDate: isoOrNull(fromDate),
      toDate: isoOrNull(toDate),
      organizationId: orgId,
      expenseCategoryId: catId,
      amountFrom: amountFrom !== '' ? Number(amountFrom) : null,
      amountTo: amountTo !== '' ? Number(amountTo) : null
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: cleanNulls(nextReq) as any }));
    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  };

  const clearFilters = () => {
    setOrganizationFilter(null);
    setExpenseCategoryFilter(null);
    setFromDate(null);
    setToDate(null);
    setAmountFrom('');
    setAmountTo('');

    const resetReq: ExpensesDetailsRequestDto = {
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: resetReq as any }));
    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  };

  const totals = useMemo(() => {
    return items.reduce(
      (acc, row) => {
        acc.totalAmount += row.amount || 0;
        acc.totalVat += row.vat || 0;
        acc.totalWithVat += row.totaL_WITH_VAT || 0;
        return acc;
      },
      { totalAmount: 0, totalVat: 0, totalWithVat: 0 }
    );
  }, [items]);

  return (
    <>
      <CardHeader className='mb-3'>
        <div className='row g-2 align-items-center mb-2'>
          {/* الفرع / المنظمة */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-building text-primary'></i>
              </span>
              <Select
                // isLoading={loadingBranches}
                value={organizationFilter}
                onChange={setOrganizationFilter}
                options={OperatingUnitOptions}
                placeholder={isAr ? 'الفرع' : 'Organization'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* فئة المصروف */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-tags text-warning'></i>
              </span>
              <Select
                // isLoading={loadingCategories}
                value={expenseCategoryFilter}
                onChange={setExpenseCategoryFilter}
                options={OptionsExpenseCategory} // TODO: ربط البيانات الحقيقية
                placeholder={isAr ? 'فئة المصروف' : 'Expense Category'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* من تاريخ */}
          <div className='col-lg-2'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={fromDate || ''}
                onChange={e => setFromDate(e.target.value)}
                placeholder={isAr ? 'من تاريخ' : 'From Date'}
              />
            </div>
          </div>

          {/* إلى تاريخ */}
          <div className='col-lg-2'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={toDate || ''}
                onChange={e => setToDate(e.target.value)}
                min={fromDate || undefined}
                placeholder={isAr ? 'إلى تاريخ' : 'To Date'}
              />
            </div>
          </div>
        </div>

        <div className='row g-2 align-items-center'>
          {/* المبلغ من */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>{isAr ? 'المبلغ من' : 'Amount From'}</span>
              <input
                type='number'
                className='form-control'
                value={amountFrom}
                onChange={e => setAmountFrom(e.target.value ? Number(e.target.value) : '')}
                min={0}
              />
            </div>
          </div>

          {/* المبلغ إلى */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>{isAr ? 'المبلغ إلى' : 'Amount To'}</span>
              <input
                type='number'
                className='form-control'
                value={amountTo}
                onChange={e => setAmountTo(e.target.value ? Number(e.target.value) : '')}
                min={amountFrom || 0}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className='col-lg-6 text-end'>
            <button
              className='btn btn-outline-secondary me-2'
              type='button'
              onClick={clearFilters}
              title={t('ClearFilters')}
              disabled={activeFiltersCount === 0}
            >
              <i className='fas fa-times fa-fw'></i>
            </button>
            <button
              className='btn btn-theme me-2'
              type='button'
              onClick={applyFilters}
              title={t('ApplyFilters')}
              disabled={activeFiltersCount === 0}
            >
              <i className='fas fa-filter fa-fw'></i>
            </button>

            <button
              className='btn btn-outline-success'
              type='button'
              onClick={() => setShowExport(true)}
              title={t('Export')}
              disabled={loading}
            >
              <i className='fas fa-file-excel fa-fw'></i>
            </button>
          </div>
        </div>
      </CardHeader>

      <Card className='mb-4'>
        <Loading loading={loading} error={error}>
          <div className='table-responsive' style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <table className='table table-hover mb-0'>
              <thead className='sticky-top' style={{ top: 0, zIndex: 10 }}>
                <tr className='bg-theme bg-opacity-35' style={{ backdropFilter: 'blur(20px)' }}>
                  <th className='small' style={{ minWidth: '120px' }}>
                    <div className='d-flex align-items-center'>{isAr ? 'رقم المصروف' : 'Expense Code'}</div>
                  </th>
                  <th className='small' style={{ minWidth: '110px' }}>
                    <div className='d-flex align-items-center'>{t('Date')}</div>
                  </th>
                  <th className='small' style={{ minWidth: '150px' }}>
                    <div className='d-flex align-items-center'>{isAr ? 'الفرع' : 'Organization'}</div>
                  </th>
                  <th className='small' style={{ minWidth: '180px' }}>
                    <div className='d-flex align-items-center'>{isAr ? 'فئة المصروف' : 'Category'}</div>
                  </th>
                  <th className='small text-end' style={{ minWidth: '110px' }}>
                    <div className='d-flex align-items-center justify-content-end'>{isAr ? 'المبلغ' : 'Amount'}</div>
                  </th>
                  <th className='small text-end' style={{ minWidth: '100px' }}>
                    <div className='d-flex align-items-center justify-content-end'>{isAr ? 'الضريبة' : 'VAT'}</div>
                  </th>
                  <th className='small text-end' style={{ minWidth: '120px' }}>
                    <div className='d-flex align-items-center justify-content-end'>{isAr ? 'الإجمالي' : 'Total'}</div>
                  </th>
                  <th className='small' style={{ minWidth: '280px' }}>
                    <div className='d-flex align-items-center'>{t('Description')}</div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {!loading && items.length > 0 ? (
                  items.map((r, idx) => {
                    const categoryName = isAr
                      ? r.categorY_NAME2 || r.categorY_NAME
                      : r.categorY_NAME || r.categorY_NAME2;

                    return (
                      <tr key={`${r.expensE_CODE || idx}-${idx}`} className='align-middle'>
                        <td>
                          <div className='fw-bold text-primary'>{safe(r.expensE_CODE)}</div>
                        </td>

                        <td>
                          <div className='fw-medium'>{r.expensE_DATE ? r.expensE_DATE.split('T')[0] : '—'}</div>
                        </td>

                        <td>
                          <div className='fw-medium'>{safe(r.orG_NAME)}</div>
                        </td>

                        <td>
                          <div className='fw-medium text-muted'>{safe(categoryName)}</div>
                        </td>

                        <td className='text-end'>
                          <div className='fw-bold'>{formatNumber(r.amount)}</div>
                        </td>

                        <td className='text-end'>
                          <div className='text-muted'>{formatNumber(r.vat)}</div>
                        </td>

                        <td className='text-end'>
                          <div className='fw-bold text-success'>{formatNumber(r.totaL_WITH_VAT)}</div>
                        </td>

                        <td>
                          <div className='d-flex align-items-center'>
                            <span className={r.description ? '' : 'text-muted fst-italic'}>
                              {safe(r.description, isAr ? 'لا يوجد وصف' : 'No description')}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : !loading ? (
                  <tr>
                    <td colSpan={8} className='text-center py-5'>
                      <div className='text-muted'>
                        <i className='fas fa-wallet fa-3x mb-3 opacity-50'></i>
                        <h6>{t('NoData')}</h6>
                        {/* <small>{t('TryChangingFilters')}</small> */}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>

              {!loading && items.length > 0 && (
                <tfoot className='fw-bold'>
                  <tr className='bg-light'>
                    <td colSpan={4} className='text-end'>
                      <span className='h6'>{t('Total')}:</span>
                    </td>
                    <td className='text-end'>
                      <span className='h6'>{formatNumber(totals.totalAmount)}</span>
                    </td>
                    <td className='text-end'>
                      <span className='h6'>{formatNumber(totals.totalVat)}</span>
                    </td>
                    <td className='text-end text-success'>
                      <span className='h6'>{formatNumber(totals.totalWithVat)}</span>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Loading>

        <CardFooter>
          <div className='d-flex justify-content-between align-items-center flex-wrap gap-3'>
            <ReportPagination reportKey={REPORT_KEY} maxVisiblePages={3} showItemsPerPage={true} />

            {!loading && items.length > 0 && (
              <div className='text-muted small'>
                <i className='fas fa-info-circle me-1'></i>
                {isAr ? 'عرض' : 'Showing'} {items.length} {isAr ? 'من' : 'of'} {meta?.totalRows || 0}{' '}
                {isAr ? 'سجل' : 'records'}
              </div>
            )}
          </div>
        </CardFooter>
      </Card>

      <ReportExportExcelModal
        show={showExport}
        onHide={() => setShowExport(false)}
        reportKey={REPORT_KEY}
        endpoint={ENDPOINT}
        columns={exportColumns}
        exportPageSize={4000}
        sheetName={({ isAr }) => (isAr ? 'تفاصيل المصروفات' : 'Expenses Details')}
        fileName={({ reportKey }) => `${reportKey}_${new Date().toISOString().slice(0, 10)}.xlsx`}
      />
    </>
  );
};

export default ExpensesDetails_Paged;
